// @flow

import { Component, throttle } from '../../jsti/core';
import Notifications from '../../constants/Notifications';
import InteractionBlocker from '../../classes/templates/InteractionBlocker';
import DataLayerEventNames from '../../constants/DataLayer/EventNames';
import EventNames from '../../constants/EventNames';

export default class BaseBody extends Component {
  #scrollHandler: function;
  #resizeHandler: function;
  scrolled = false;
  main: HTMLElement;
  interactionBlocker: HTMLElement;

  /**
   * Initialize the component.
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    if (pEl !== document.body) {
      throw new Error('BodyComponent should be added on the body');
    }
    super.bind(pEl);

    const main = pEl.querySelector('main');
    if (!main) {
      throw new Error('A main is required in the DOM');
    }
    this.main = main;

    this.interactionBlocker = InteractionBlocker.template();

    this.#scrollHandler = throttle(this.handleScrollEvents.bind(this), 10);
    this.#resizeHandler = throttle(this.handleResizeEvents.bind(this), 10);

    document.addEventListener(EventNames.SCROLL, this.#scrollHandler, { passive: true });
    window.addEventListener(EventNames.RESIZE, this.#resizeHandler);

    this.addListener(Notifications.ADD_PAGE_INTERACTION, this.addPageInteraction);
    this.addListener(Notifications.REMOVE_PAGE_INTERACTION, this.removePageInteraction);
    this.addListener(Notifications.TOGGLE_SCROLLBAR_STATE, this.toggleScrollBlocker);
    this.addListener(Notifications.ALLOW_SCROLLBAR, this.removeScrollBlocker);
    this.addListener(Notifications.DISALLOW_SCROLLBAR, this.addScrollBlocker);
  }

  /**
   * Executes when the user scrolls.
   */
  handleScrollEvents(): void {
    let scrollTop: number = window.pageYOffset;
    if (!window.pageYOffset) {
      scrollTop = this.el.scrollTop;
    }

    this.scrolled = Boolean(scrollTop);
    this.notify(Notifications.SCROLL, {
      scrollTop,
      scrolled: this.scrolled
    });
  }

  /**
   * Executes when the user resizes the window.
   */
  handleResizeEvents(): void {
    this.notify(Notifications.RESIZE);
  }

  /**
   * The user can interact with the page.
   */
  addPageInteraction(): void {
    this.interactionBlocker.remove();
  }

  /**
   * The user can not interact with the page
   * Header and footer still have interaction.
   */
  removePageInteraction(): void {
    this.main.appendChild(this.interactionBlocker);
  }

  /**
   * Toggle the state of the scrollbar.
   */
  toggleScrollBlocker({ keepCurrentPosition }: { keepCurrentPosition?: boolean }): void {
    if (document && document.documentElement) {
      document.documentElement.classList.toggle('no-scroll');

      if (keepCurrentPosition) {
        document.documentElement.classList.toggle('no-scroll--stay');
      }
    }
  }

  /**
   * Disallow use of the scrollbar.
   */
  addScrollBlocker(): void {
    if (document && document.documentElement) {
      document.documentElement.classList.add('no-scroll');
    }
  }

  /**
   * Allow use of the scrollbar.
   */
  removeScrollBlocker(): void {
    if (document && document.documentElement) {
      document.documentElement.classList.remove('no-scroll');
    }
  }

  /**
   * Destroy all event listeners.
   */
  destroy() {
    document.removeEventListener(EventNames.SCROLL, this.#scrollHandler);
    document.removeEventListener(EventNames.RESIZE, this.#resizeHandler);
    super.destroy();
  }
}
